import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getCategoryById, getVideosByIds, formatCount } from "@/utils/video";
import { heroVideos } from "@/mocks/videos";

const featured = getVideosByIds(heroVideos);

export default function HeroSection() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % featured.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  if (featured.length === 0) return null;
  const current = featured[index];
  const category = getCategoryById(current.category);

  return (
    <section className="w-full bg-background-100/60">
      <div className="mx-auto max-w-7xl px-4 py-6 md:px-8 md:py-10">
        <div className="grid gap-6 lg:grid-cols-[1.05fr_1fr] lg:items-stretch">
          {/* left intro panel */}
          <div className="flex animate-fadeUp flex-col justify-center rounded-lg border border-background-200 bg-background-50 p-6 md:p-10">
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
              <i className="ri-heart-3-fill" /> 糖心Vlog · 真实生活记录
            </span>
            <h1 className="mt-5 font-heading text-3xl font-extrabold leading-tight text-foreground-950 md:text-5xl">
              记录真实生活，
              <br />
              分享每一个精彩瞬间
            </h1>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-foreground-600 md:text-base">
              日常记录、旅行体验、个人见解。糖心Vlog 用镜头收藏平凡日子里的温度，
              把真实的自己拍给你看。
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/category/daily"
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-circle-line text-base" /> 开始探索视频
              </Link>
              <Link
                to="/special/mountain-sea"
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-bookmark-3-line text-base" /> 逛逛精选专题
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-4">
              <div>
                <p className="font-heading text-2xl font-extrabold text-foreground-950">24</p>
                <p className="text-xs text-foreground-500">精选视频</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-extrabold text-foreground-950">3</p>
                <p className="text-xs text-foreground-500">内容分类</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-extrabold text-foreground-950">60.8万</p>
                <p className="text-xs text-foreground-500">累计播放</p>
              </div>
            </div>
          </div>

          {/* right focus carousel */}
          <div className="relative h-[340px] animate-fadeUp overflow-hidden rounded-lg border border-background-200 sm:h-[420px] lg:h-auto lg:min-h-[460px]">
            {
              featured.map((v, i) => (
                <img
                  key={v.id}
                  src={v.cover}
                  alt={v.title}
                  className={`absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-700 ${
                    i === index ? "opacity-100" : "opacity-0"
                  }`}
                />
              ))
            }
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/30 to-foreground-950/10" />

            <div className="absolute right-4 top-4 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIndex((i) => (i - 1 + featured.length) % featured.length)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md bg-background-50/85 text-foreground-900 transition-colors hover:bg-background-50"
                aria-label="上一个"
              >
                <i className="ri-arrow-left-s-line text-xl" />
              </button>
              <button
                type="button"
                onClick={() => setIndex((i) => (i + 1) % featured.length)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md bg-background-50/85 text-foreground-900 transition-colors hover:bg-background-50"
                aria-label="下一个"
              >
                <i className="ri-arrow-right-s-line text-xl" />
              </button>
            </div>

            <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
              <span className="inline-flex items-center gap-1 rounded-md bg-accent-500 px-2 py-0.5 text-xs font-medium text-background-50">
                <i className="ri-flashlight-fill" /> 焦点推荐
              </span>
              <h2 className="mt-3 max-w-xl font-heading text-xl font-bold text-background-50 md:text-2xl">
                {current.title}
              </h2>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-background-100/90">
                <span className="flex items-center gap-1">
                  <i className="ri-folder-line" /> {category?.name}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-play-circle-line" /> {formatCount(current.views)} 次播放
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line" /> {current.duration}
                </span>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <Link
                  to={`/detail/${current.id}`}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-play-fill" /> 立即观看
                </Link>
                <div className="flex items-center gap-2">
                  {featured.map((v, i) => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => setIndex(i)}
                      className={`h-1.5 cursor-pointer rounded-full transition-all ${
                        i === index ? "w-6 bg-background-50" : "w-1.5 bg-background-50/50"
                      }`}
                      aria-label={`切换到第 ${i + 1} 个`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}